<template>
  <div class="s-review">
    <div class="s-review__score">
      <SReviewScoreAtom :model-value="review.ratingAverage" />
      <SButton
        v-if="editable"
        action-type="tertiary"
        :disabled="loading"
        @click="editReview"
        >{{ tr("client.reviews.review_detailed_edit") }}</SButton
      >
    </div>

    <h4 v-if="review.summary" class="s-review__title" v-html="review.summary" />
    <p class="s-review__description" v-html="review.explanation" />
    <div v-if="review.reviewImages.length > 0" class="s-review__images">
      <button
        class="s-review__images__button"
        type="button"
        @click="showLightbox"
      >
        <img
          :alt="tr('client.reviews.review_images_alt')"
          class="s-review__images__image"
          loading="lazy"
          :src="
            useCloudinary(review.reviewImages[0]!.path, {
              width: 640,
              height: 360,
            })
          "
        />
        <span
          v-if="review.reviewImages.length > 1"
          class="s-review__images__count"
        >
          <i class="fa-solid fa-images" />
          {{ review.reviewImages.length }}
        </span>
      </button>
      <Teleport v-if="lightboxMounted" to="body">
        <lazyVueEasyLightbox
          class="s-review__images__lightbox"
          dblclick-disabled
          :imgs="lightboxImgUrls"
          move-disabled
          pinch-disabled
          :visible="lightboxVisible"
          zoom-disabled
          @hide="closeLightbox"
        >
          <template #toolbar>&nbsp;</template>
        </lazyVueEasyLightbox>
      </Teleport>
    </div>
    <div
      v-if="sourceTranslation"
      class="s-review__source"
      v-html="sourceTranslation"
    />
    <div class="s-review__info">
      <template v-if="displayDate">
        <span>{{ displayDate }}</span>
        <span
          v-if="review.userId || !isInternal || lastEditedDate"
          class="s-review__info__dot"
        />
      </template>

      <template v-if="review.userId || !isInternal">
        <span v-if="review.userId" class="s-review__info__verified">
          <i
            class="s-review__info__verified__check fa-regular fa-circle-check"
          />
          {{ tr("client.reviews.review_detailed_verified_customer") }}
        </span>
        <span v-else-if="!isInternal">{{ externalSourceTranslation }}</span>
      </template>

      <span
        v-if="(review.userId || !isInternal) && lastEditedDate"
        class="s-review__info__dot"
      />

      <span v-if="lastEditedDate">
        {{
          tr("client.reviews.review_detailed_last_edited_on", {
            date: lastEditedDate,
          })
        }}
      </span>
    </div>
    <div
      v-if="review.customerReply && review.customerName"
      class="s-review__customer-reply"
    >
      <p class="s-review__customer-reply__title">
        <i class="s-review__customer-reply__title__icon fa-solid fa-reply" />
        {{
          tr("client.reviews.review_detailed_customer_response_from", {
            customerName: review.customerName,
          })
        }}
      </p>
      <p class="s-review__customer-reply__reply">
        {{ review.customerReply }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useScrollLock } from "@vueuse/core";
import { toLower } from "ramda";
import { objectToSnake } from "ts-case-convert";
import { computed, defineAsyncComponent, ref } from "vue";

import { SButton, SReviewScoreAtom } from "@solvari/common-fe";
import { useCloudinary, ziggyUnsafe } from "@solvari/common-fe/integrations";
import { useI18nReviews } from "@solvari/translations";
import { dayjs } from "@solvari/utils";

const props = withDefaults(
  defineProps<{
    editable?: boolean;
    loading?: boolean;
    review: App.DTO.Customer.Review.CustomerReviewDTO;
  }>(),
  {
    editable: false,
    loading: false,
  },
);

const emit =
  defineEmits<(event: "editReview", customerId: number | null) => void>();

const { tr } = useI18nReviews();

const internalSources: App.Domain.Enums.Customer.Review.ReviewSourceEnum[] = [
  "DEFAULT",
  "DIRECT",
];

// Click-only → lazy; static import ships 31 kB to every page with reviews.
const lazyVueEasyLightbox = defineAsyncComponent(
  () => import("vue-easy-lightbox"),
);

const lightboxMounted = ref(false);
// <html> scrolls, not <body> → the lightbox's own body lock misses it.
const lightboxVisible = useScrollLock(
  typeof document !== "undefined" ? document.documentElement : null,
);

const hasAuthor = computed(() => !!props.review.author);
const isInternal = computed(
  () => props.review.source && internalSources.includes(props.review.source),
);

function buildLink(url: string, text: string) {
  return `<a target="_blank" href="${url}">${text}</a>`;
}

const sourceTranslation = computed(() => {
  const companyUrl = props.review.customerSlug
    ? ziggyUnsafe("mainSite.companyWall.companyWallDetails", {
        slug: props.review.customerSlug,
      })
    : null;

  const company =
    companyUrl && !props.review.noLinks
      ? buildLink(companyUrl, props.review.customerName)
      : props.review.customerName;

  const product =
    props.review.productName && !props.review.noLinks
      ? props.review.productLink
        ? buildLink(props.review.productLink, props.review.productName)
        : props.review.productName
      : null;

  if (product && hasAuthor.value && props.review.city) {
    return tr(
      "client.reviews.review_source_author_city_product_company",
      objectToSnake({
        authorName: props.review.author!,
        authorCity: props.review.city,
        product,
        company,
      }),
    );
  }

  if (product && hasAuthor.value) {
    return tr(
      "client.reviews.review_source_author_product_company",
      objectToSnake({
        authorName: props.review.author!,
        product,
        company,
      }),
    );
  }

  if (product) {
    return tr("client.reviews.review_source_product_company", {
      product,
      company,
    });
  }

  if (hasAuthor.value && props.review.city) {
    return tr(
      "client.reviews.review_source_author_city_company",
      objectToSnake({
        authorName: props.review.author!,
        authorCity: props.review.city,
        company,
      }),
    );
  }

  if (hasAuthor.value) {
    return tr(
      "client.reviews.review_source_author_company",
      objectToSnake({
        authorName: props.review.author!,
        company,
      }),
    );
  }

  return tr("client.reviews.review_source_company", { company });
});
const externalSourceTranslation = computed(() => {
  if (
    props.review.source &&
    props.review.source !== "DEFAULT" &&
    props.review.source !== "DIRECT"
  ) {
    return props.review.source === "UNVERIFIED_INVITE"
      ? tr(`client.reviews.${toLower(props.review.source)}_via`, {
          customerName: props.review.customerName,
        })
      : tr(`client.reviews.${toLower(props.review.source)}_via`);
  }
  return null;
});

const displayDate = computed(() => {
  if (!props.review.createdAt) {
    return null;
  }
  return dayjs(props.review.createdAt).format("D MMMM YYYY");
});
const lightboxImgUrls = computed(() => {
  if (props.review.reviewImages.length === 0) {
    return [];
  }
  return props.review.reviewImages.map((img) =>
    useCloudinary(img.path, { width: "auto", height: "auto" }),
  );
});

const lastEditedDate = computed(() => {
  if (!props.review.lastEditedAt) {
    return null;
  }
  return dayjs(props.review.lastEditedAt).format("D MMMM YYYY");
});

function showLightbox() {
  lightboxMounted.value = true;
  lightboxVisible.value = true;
}
function closeLightbox() {
  lightboxVisible.value = false;
}

function editReview() {
  emit("editReview", props.review.id);
}
</script>

<style lang="postcss">
@import "./review.css";
</style>
